import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import { Sidebar } from '../modules/Sidebar';
import {
    handleSwiperProgressBar,
    swiperNavigation,
} from '../modules/swiperModules';

class DesignLabSidebar {
    constructor() {
        if (typeof ajax === 'undefined') {
            return;
        }

        this.sidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });
        this.contentHost = document.createElement('div');
        this.contentHost.className = 'h-full w-full max-w-[720px]';
        this.sidebar.content.appendChild(this.contentHost);
        this.isLoading = false;
        this.teamSwiper = null;
        this.gallerySwipers = [];
        this.processSwiper = null;
        this.handleTeamResize = this.handleTeamResize.bind(this);

        document.addEventListener('click', this.onDocumentClick.bind(this), true);
    }

    async onDocumentClick(event) {
        const trigger = event.target.closest('[data-open-design-lab-sidebar]');

        if (!trigger) {
            return;
        }

        event.preventDefault();
        await this.openSidebar();
    }

    async openSidebar() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.destroyTeamSwipers();
        this.destroyProcessSwiper();
        this.contentHost.innerHTML =
            '<div class="flex h-full w-full max-w-[720px] items-center justify-center bg-background-primary"><p class="text-regular text-text-secondary">...</p></div>';

        try {
            const html = await this.fetchSidebarHtml();

            if (!html) {
                return;
            }

            this.applySidebarHtml(html);
            this.sidebar.open();
        } finally {
            this.isLoading = false;
        }
    }

    async fetchSidebarHtml() {
        const formData = new FormData();
        formData.append('action', 'mda_design_lab_sidebar');
        formData.append('nonce', ajax.nonce);

        try {
            const response = await fetch(ajax.url, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                return '';
            }

            const result = await response.json();

            if (!result?.success || typeof result?.data?.html !== 'string') {
                return '';
            }

            return result.data.html.trim();
        } catch {
            return '';
        }
    }

    applySidebarHtml(html) {
        this.contentHost.innerHTML = html;
        this.initTeamSwiper();
        this.initProcessSwiper();
    }

    initTeamSwiper() {
        const root = this.contentHost.querySelector('[data-design-lab-sidebar]');

        if (!root) {
            return;
        }

        const swiperEl = root.querySelector('.design-lab-sidebar__team-swiper');
        const arrowsEl = root.querySelector('.design-lab-sidebar__team-arrows');
        const slideCount = swiperEl
            ? swiperEl.querySelectorAll('.swiper-slide').length
            : 0;

        this.destroyTeamSwipers();

        if (arrowsEl) {
            arrowsEl.classList.toggle('hidden', slideCount <= 1);
        }

        if (swiperEl && slideCount > 0) {
            const swiperConfig = {
                slidesPerView: 1.08,
                spaceBetween: 12,
                autoHeight: true,
                allowTouchMove: false,
                rewind: true,
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                a11y: {
                    prevSlideMessage: 'Poprzedni członek zespołu',
                    nextSlideMessage: 'Następny członek zespołu',
                },
            };

            if (arrowsEl && slideCount > 1) {
                swiperConfig.modules = [Navigation];
                Object.assign(swiperConfig, swiperNavigation(arrowsEl));
            }

            this.teamSwiper = new Swiper(swiperEl, swiperConfig);

            this.teamSwiper.on('slideChangeTransitionEnd', () => {
                this.refreshGallerySwipers();
            });

            window.addEventListener('resize', this.handleTeamResize);
            this.handleTeamResize();
        }

        this.initGallerySwipers();
        this.refreshGallerySwipers();
    }

    handleTeamResize() {
        if (this.teamSwiper) {
            this.teamSwiper.update();
        }

        this.refreshGallerySwipers();
    }

    refreshGallerySwipers() {
        this.gallerySwipers.forEach(swiper => {
            swiper.update();
        });
    }

    initGallerySwipers() {
        const root = this.contentHost.querySelector('[data-design-lab-sidebar]');

        if (!root) {
            return;
        }

        if (this.gallerySwipers.length) {
            this.gallerySwipers.forEach(swiper => {
                swiper.destroy(true, true);
            });
            this.gallerySwipers = [];
        }

        root.querySelectorAll('.team-member-gallery__swiper').forEach(swiperEl => {
            const sectionRoot = swiperEl.closest('.team-member-gallery');
            const slides = swiperEl.querySelectorAll('.swiper-slide');

            if (slides.length <= 1) {
                return;
            }

            const footerEl = sectionRoot?.querySelector(
                '.team-member-gallery__footer'
            );
            const arrowsEl = footerEl?.querySelector('.slider-arrows');
            const paginationEl = footerEl?.querySelector('.slider-pagination');

            const swiperConfig = {
                nested: true,
                slidesPerView: 2,
                spaceBetween: 20,
                a11y: {
                    prevSlideMessage: 'Poprzednie zdjęcie',
                    nextSlideMessage: 'Następne zdjęcie',
                },
            };

            if (arrowsEl && slides.length > 2) {
                swiperConfig.modules = [Navigation];
                Object.assign(swiperConfig, swiperNavigation(arrowsEl));
            }

            const swiper = new Swiper(swiperEl, swiperConfig);

            if (paginationEl && slides.length > 2) {
                handleSwiperProgressBar(paginationEl, swiper);
            }

            this.gallerySwipers.push(swiper);
        });
    }

    destroyTeamSwipers() {
        window.removeEventListener('resize', this.handleTeamResize);

        if (this.teamSwiper) {
            this.teamSwiper.destroy(true, true);
            this.teamSwiper = null;
        }

        if (this.gallerySwipers.length) {
            this.gallerySwipers.forEach(swiper => {
                swiper.destroy(true, true);
            });
            this.gallerySwipers = [];
        }
    }

    initProcessSwiper() {
        const root = this.contentHost.querySelector('[data-design-lab-sidebar]');

        if (!root) {
            return;
        }

        const slider = root.querySelector('.design-lab-sidebar__process-slider');

        if (!slider || slider.querySelectorAll('.swiper-slide').length <= 1) {
            return;
        }

        const slidesImages = root.querySelectorAll('[data-process-slide-image]');

        this.destroyProcessSwiper();

        this.processSwiper = new Swiper(slider, {
            direction: 'vertical',
            allowTouchMove: false,
            slideToClickedSlide: true,
            slideActiveClass: 'active',
            modules: [Autoplay],
            autoplay: {
                disableOnInteraction: false,
                waitForTransition: false,
                delay: 6000,
            },
        });

        this.processSwiper.on('slideChange', swiper => {
            const { activeIndex } = swiper;

            slidesImages.forEach((slide, index) => {
                if (index === activeIndex) {
                    setTimeout(() => {
                        slide.classList.add('active');
                    }, 300);
                } else {
                    slide.classList.remove('active');
                }
            });
        });
    }

    destroyProcessSwiper() {
        if (this.processSwiper) {
            this.processSwiper.destroy(true, true);
            this.processSwiper = null;
        }
    }
}

new DesignLabSidebar();
